
import { Component, Vue, Prop } from "vue-property-decorator";
import {
  QUERY_GET_CREATOR_APPLICATIONS,
  MUTATION_APPROVE_APPLICATION,
  MUTATION_REJECT_APPLICATION
} from "./graphql";

interface Application {
  id: string;
  firstname: string;
  pseudo: string;
  followers: string;
  email: string;
  invitationCode: string | null;
  inviterUsername: string | null;
  tipshoutUsername: string | null;
  status: string;
  createdAt: string;
}

@Component
export default class CreatorApplicationList extends Vue {
  @Prop({ default: "invitations" }) mode!: "invitations" | "applications";

  loading = false;
  approvingId: string | null = null;
  rejectingId: string | null = null;
  applications: Application[] = [];
  usernameInputs: Record<string, string> = {};
  statusFilter: string | null = "pending";
  rejectDialog = false;
  rejectTarget: Application | null = null;

  get title(): string {
    return this.mode === "invitations"
      ? "Creator Invitations"
      : "Creator Applications";
  }

  get tableHeaders() {
    const base = [
      { text: "Date", value: "createdAt", width: 120 },
      { text: "Prénom Nom", value: "firstname" },
      { text: "Pseudo", value: "pseudo" },
      { text: "Followers", value: "followers", width: 100 },
      { text: "Email", value: "email" },
      { text: "Statut", value: "status", width: 120 },
      { text: "Actions", value: "actions", sortable: false, width: 380 }
    ];
    if (this.mode === "invitations") {
      base.splice(5, 0, {
        text: "Invité par",
        value: "inviterUsername",
        width: 130
      });
    }
    return base;
  }

  statusOptions = [
    { text: "En attente", value: "pending" },
    { text: "Approuvé", value: "approved" },
    { text: "Rejeté", value: "rejected" }
  ];

  get filteredApplications(): Application[] {
    const byMode =
      this.mode === "invitations"
        ? this.applications.filter(a => !!a.invitationCode)
        : this.applications.filter(a => !a.invitationCode);
    if (!this.statusFilter) return byMode;
    return byMode.filter(a => a.status === this.statusFilter);
  }

  mounted() {
    this.fetchApplications();
  }

  async fetchApplications() {
    this.loading = true;
    try {
      const { data } = await this.$apollo.query({
        query: QUERY_GET_CREATOR_APPLICATIONS,
        fetchPolicy: "network-only"
      });
      this.applications = data.creatorApplications;
      this.applications.forEach(a => {
        if (!(a.id in this.usernameInputs)) {
          this.$set(this.usernameInputs, a.id, "");
        }
      });
    } catch (err) {
      this.$store.commit("setSnackMessageWithColor", {
        message: err,
        color: "error"
      });
    } finally {
      this.loading = false;
    }
  }

  async approve(item: Application) {
    const username = (this.usernameInputs[item.id] || "").trim();
    if (!username) return;
    this.approvingId = item.id;
    try {
      await this.$apollo.mutate({
        mutation: MUTATION_APPROVE_APPLICATION,
        variables: { input: { id: item.id, tipshoutUsername: username } }
      });
      this.$store.commit("setSnackMessageWithColor", {
        message: `✅ ${item.pseudo} approuvé en tant que @${username}`,
        color: "success"
      });
      await this.fetchApplications();
    } catch (err: any) {
      const message =
        (err &&
          err.graphQLErrors &&
          err.graphQLErrors[0] &&
          err.graphQLErrors[0].message) ||
        (err && err.message) ||
        "Une erreur est survenue";
      this.$store.commit("setSnackMessageWithColor", {
        message,
        color: "error"
      });
    } finally {
      this.approvingId = null;
    }
  }

  reject(item: Application) {
    this.rejectTarget = item;
    this.rejectDialog = true;
  }

  async confirmReject() {
    if (!this.rejectTarget) return;
    this.rejectDialog = false;
    this.rejectingId = this.rejectTarget.id;
    try {
      await this.$apollo.mutate({
        mutation: MUTATION_REJECT_APPLICATION,
        variables: { id: this.rejectTarget.id }
      });
      this.$store.commit("setSnackMessageWithColor", {
        message: `Candidature de ${this.rejectTarget.pseudo} rejetée`,
        color: "info"
      });
      await this.fetchApplications();
    } catch (err) {
      this.$store.commit("setSnackMessageWithColor", {
        message: err,
        color: "error"
      });
    } finally {
      this.rejectingId = null;
      this.rejectTarget = null;
    }
  }

  statusColor(status: string): string {
    if (status === "approved") return "success";
    if (status === "rejected") return "error";
    return "orange";
  }

  statusLabel(status: string): string {
    if (status === "approved") return "Approuvé";
    if (status === "rejected") return "Rejeté";
    return "En attente";
  }

  formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit"
    });
  }
}
